export const sessionStorageUtils = {
    /**
     * Set a value in session storage
     * @param key - The key to store the value under
     * @param value - The value to store (will be JSON stringified)
     */
    set: (key: string, value: any): void => {
      if (typeof window !== "undefined") {
        try {
          sessionStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
          console.error("Error setting session storage item:", error);
        }
      }
    },
  
    /**
     * Get a value from session storage
     * @param key - The key to retrieve the value from
     * @param defaultValue - Optional default value if key doesn't exist
     * @returns The parsed value or null if not found
     */
    get: <T = any>(key: string, defaultValue?: T): T | null => {
      if (typeof window !== "undefined") {
        try {
          const item = sessionStorage.getItem(key);
          if (item === null) {
            return defaultValue !== undefined ? defaultValue : null;
          }
          return JSON.parse(item);
        } catch (error) {
          console.error("Error getting session storage item:", error);
          return defaultValue !== undefined ? defaultValue : null;
        }
      }
      return defaultValue !== undefined ? defaultValue : null;
    },
  
    /**
     * Remove a specific key from session storage
     * @param key - The key to remove
     */
    remove: (key: string): void => {
      if (typeof window !== "undefined") {
        try {
          sessionStorage.removeItem(key);
        } catch (error) {
          console.error("Error removing session storage item:", error);
        }
      }
    },
  
    /**
     * Clear all session storage
     */
    clear: (): void => {
      if (typeof window !== "undefined") {
        try {
          sessionStorage.clear();
        } catch (error) {
          console.error("Error clearing session storage:", error);
        }
      }
    },
  
    /**
     * Check if a key exists in session storage
     * @param key - The key to check
     * @returns True if the key exists, false otherwise
     */
    has: (key: string): boolean => {
      if (typeof window !== "undefined") {
        try {
          return sessionStorage.getItem(key) !== null;
        } catch (error) {
          console.error("Error checking session storage key:", error);
          return false;
        }
      }
      return false;
    },
  
    /**
     * Get all keys from session storage
     * @returns Array of all keys in session storage
     */
    keys: (): string[] => {
      if (typeof window !== "undefined") {
        try {
          return Object.keys(sessionStorage);
        } catch (error) {
          console.error("Error getting session storage keys:", error);
          return [];
        }
      }
      return [];
    },
  };
  
  // Local Storage Utility Functions
export const localStorageUtils = {
  set: (key: string, value: any): void => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error("Error setting local storage item:", error);
      }
    }
  },

  get: <T = any>(key: string, defaultValue?: T): T | null => {
    if (typeof window !== "undefined") {
      try {
        const item = localStorage.getItem(key);
        if (item === null) {
          return defaultValue !== undefined ? defaultValue : null;
        }
        return JSON.parse(item);
      } catch (error) {
        console.error("Error getting local storage item:", error);
        return defaultValue !== undefined ? defaultValue : null;
      }
    }
    return defaultValue !== undefined ? defaultValue : null;
  },

  remove: (key: string): void => {
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error("Error removing local storage item:", error);
      }
    }
  },

  /**
   * Clear all session storage
   */
  clear: (): void => {
    if (typeof window !== "undefined") {
      try {
        localStorage.clear();
      } catch (error) {
        console.error("Error clearing local storage:", error);
      }
    }
  },

  has: (key: string): boolean => {
    if (typeof window !== "undefined") {
      try {
        return localStorage.getItem(key) !== null;
      } catch (error) {
        console.error("Error checking local storage key:", error);
        return false;
      }
    }
    return false;
  },

  keys: (): string[] => {
    if (typeof window !== "undefined") {
      try {
        return Object.keys(localStorage);
      } catch (error) {
        console.error("Error getting local storage keys:", error);
        return [];
      }
    }
    return [];
  },
};